import { useState } from "react";
import { Box, Chip, Popover, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useThemeMode } from "../../../theme/ThemeModeProvider";
import { chatRailTokens } from "../tokens";

export interface MemoryCitation {
  id: string;
  memory_id: string;
  ord: number;
  start_char: number;
  end_char: number;
  excerpt: string;
}

export function MemoryCitationChip({
  citation,
  agentId,
}: {
  citation: MemoryCitation;
  agentId: string;
}) {
  const navigate = useNavigate();
  const { mode } = useThemeMode();
  const T = chatRailTokens(mode);
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const open = Boolean(anchor);

  return (
    <>
      <Chip
        size="small"
        clickable
        label={`#${citation.ord + 1}`}
        aria-label={`Memory citation ${citation.ord + 1}`}
        onClick={(e) => setAnchor(e.currentTarget)}
        sx={{
          mx: 0.25,
          height: 18,
          fontSize: T.fs_micro,
          color: T.accent,
          bgcolor: T.accentSoft,
          border: `1px solid ${T.accentBorder}`,
          "& .MuiChip-label": { px: 0.75 },
        }}
      />
      <Popover
        open={open}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        slotProps={{ paper: { sx: { maxWidth: 360, p: 1.5 } } }}
      >
        <Typography variant="caption" sx={{ color: T.textDim, fontSize: T.fs_micro }}>
          Memory excerpt
        </Typography>
        <Typography
          sx={{
            fontSize: T.fs_meta,
            color: T.text,
            mt: 0.5,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
        >
          {citation.excerpt || "(no excerpt available)"}
        </Typography>
        <Box mt={1}>
          <Box
            component="button"
            type="button"
            onClick={() => {
              setAnchor(null);
              navigate(`/agents/${agentId}/memory#mem-${citation.memory_id}`);
            }}
            sx={{
              border: "none",
              bgcolor: "transparent",
              cursor: "pointer",
              color: T.accent,
              fontSize: T.fs_micro,
              p: 0,
              "&:hover": { textDecoration: "underline" },
            }}
          >
            Open memory →
          </Box>
        </Box>
      </Popover>
    </>
  );
}

export function renderTextWithCitations(
  text: string,
  citations: MemoryCitation[],
  agentId: string,
): React.ReactNode[] {
  if (citations.length === 0) return [text];
  const sorted = [...citations].sort((a, b) => a.end_char - b.end_char);
  const out: React.ReactNode[] = [];
  let cursor = 0;
  sorted.forEach((c) => {
    const safeEnd = Math.max(cursor, Math.min(c.end_char, text.length));
    if (safeEnd > cursor) out.push(text.slice(cursor, safeEnd));
    out.push(<MemoryCitationChip key={c.id} citation={c} agentId={agentId} />);
    cursor = safeEnd;
  });
  if (cursor < text.length) out.push(text.slice(cursor));
  return out;
}
